import { authenticatedFetch } from "./api";
import { API_HOST } from "./constants";
import {
  applyAssetStatusResult,
  getEffectiveAssetMeta,
  normalizeVideoAssetStatus,
} from "./guaVideoAssetStatus";
import {
  getVideoAssetApiBase,
  normalizeVideoProvider,
} from "./projectRuntime";
import {
  isDreaminaProxySeedance20Provider,
  requireDreaminaProxyOriginalUrl,
} from "./dreaminaProxyReferences";

const DEFAULT_ASSET_POLL_ATTEMPTS = 18;
const DEFAULT_ASSET_POLL_INTERVAL_MS = 3_000;

const firstText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const sleep = (delayMs) => new Promise((resolve) => {
  setTimeout(resolve, Math.max(0, Number(delayMs) || 0));
});

const ensureNotCancelled = (shouldContinue) => {
  if (shouldContinue?.() !== false) return;
  const error = new Error("素材状态查询已取消");
  error.name = "AbortError";
  throw error;
};

export const pollVideoAssetUntilTerminal = async ({
  provider,
  projectId,
  url = "",
  assetId,
  request = authenticatedFetch,
  onStatus,
  shouldContinue = () => true,
  maxAttempts = DEFAULT_ASSET_POLL_ATTEMPTS,
} = {}) => {
  const normalizedProvider = normalizeVideoProvider(provider);
  const apiBase = getVideoAssetApiBase(normalizedProvider);
  const normalizedAssetId = firstText(assetId).replace(/^asset:\/\//i, "");
  if (!apiBase) throw new Error("当前视频厂商不支持素材状态查询");
  if (!normalizedAssetId) throw new Error("素材状态查询缺少 assetId");

  let lastError = null;
  const attempts = Math.max(1, Number(maxAttempts) || DEFAULT_ASSET_POLL_ATTEMPTS);
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    ensureNotCancelled(shouldContinue);
    try {
      const result = await request(`${API_HOST}${apiBase}/status`, {
        method: "POST",
        body: JSON.stringify({
          projectId: projectId ? Number(projectId) : undefined,
          url: firstText(url) || undefined,
          assetId: normalizedAssetId,
        }),
      });
      ensureNotCancelled(shouldContinue);
      const returnedId = firstText(getEffectiveAssetMeta(result || {}, normalizedProvider).assetId)
        .replace(/^asset:\/\//i, "");
      if (returnedId && returnedId !== normalizedAssetId) {
        throw new Error("素材状态返回的 assetId 不一致");
      }
      const applied = applyAssetStatusResult({}, result, normalizedProvider);
      if (!applied.accepted) throw new Error("素材状态返回格式不正确");
      const status = normalizeVideoAssetStatus(applied.status);
      onStatus?.({ assetId: normalizedAssetId, status, url, attempt, result });
      if (status === "Active" || status === "Failed") {
        return { assetId: normalizedAssetId, status, result };
      }
      lastError = null;
    } catch (error) {
      ensureNotCancelled(shouldContinue);
      lastError = error;
    }
    if (attempt < attempts - 1) await sleep(DEFAULT_ASSET_POLL_INTERVAL_MS);
  }
  throw lastError || new Error("素材状态查询超时，请稍后重试");
};

// New overseas 2.0 submissions are re-ingested into the Dreamina v3 library by
// the backend. Never send an old EngGuaVideo/GuaOmini asset ID as if it belonged
// to that library. All six video entry points share this final client boundary.
export const preflightVideoAssetContents = async ({ provider, contents } = {}) => {
  if (!isDreaminaProxySeedance20Provider(provider) || !Array.isArray(contents)) return contents;
  return contents.map((item) => {
    if (!['image_url', 'video_url', 'audio_url'].includes(item?.type)) return item;
    const sanitized = { ...item, url: requireDreaminaProxyOriginalUrl(item) };
    delete sanitized.assetId;
    delete sanitized.engGuaVideoAssetId;
    delete sanitized.guaOminiAssetId;
    delete sanitized.dreaminaProxyAssetId;
    return sanitized;
  });
};

export const mergePreparedVideoAssetMetadata = (items) => items;

export const sanitizeVideoAuxiliaryReferenceItems = (items) => items;
